/**
 * Climate panel — environmental anomaly detection.
 *
 * Aggregates events from weather, eonet, firms, usgs, gdacs sources.
 * Category tabs: All, Severe Weather, Wildfires, Earthquakes, Volcanic, Other.
 * Sorted by severity then recency. Click → fly to event location.
 */

import { appStore } from '@/core/store';
import { relativeTime } from '@/utils/time';
import { SEVERITY_CSS_COLORS, SEVERITY_RANK } from '@/utils/severity';
import { getMap } from '@/map/engine';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

const CLIMATE_SOURCES = new Set(['weather', 'eonet', 'firms', 'usgs', 'gdacs']);

type ClimateCategory = 'all' | 'weather' | 'wildfire' | 'earthquake' | 'volcanic' | 'other';

const CATEGORY_TABS: { label: string; value: ClimateCategory }[] = [
  { label: 'All', value: 'all' },
  { label: 'Severe Weather', value: 'weather' },
  { label: 'Wildfires', value: 'wildfire' },
  { label: 'Earthquakes', value: 'earthquake' },
  { label: 'Volcanic', value: 'volcanic' },
  { label: 'Other', value: 'other' },
];

const CATEGORY_ICONS: Record<ClimateCategory, string> = {
  all: '',
  weather: '\u26C8',     // thunder cloud
  wildfire: '\uD83D\uDD25', // fire
  earthquake: '\uD83C\uDF0D', // globe
  volcanic: '\uD83C\uDF0B', // volcano
  other: '\u2753',       // question
};

function inferClimateCategory(ev: NormalizedEvent): ClimateCategory {
  if (ev.source === 'weather' || ev.type === 'weather') return 'weather';
  if (ev.source === 'firms' || ev.type === 'fire') return 'wildfire';
  if (ev.source === 'usgs' || ev.type === 'earthquake') return 'earthquake';

  const tags = ev.tags ?? [];
  const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();

  if (tags.includes('volcanic') || text.includes('volcano') || text.includes('eruption') || text.includes('volcanic')) return 'volcanic';
  if (tags.includes('wildfire') || text.includes('wildfire') || text.includes('fire')) return 'wildfire';
  if (tags.includes('earthquake') || text.includes('earthquake') || text.includes('seismic')) return 'earthquake';
  if (tags.includes('storm') || text.includes('storm') || text.includes('hurricane') || text.includes('typhoon') || text.includes('tornado') || text.includes('flood')) return 'weather';

  return 'other';
}

export function createClimatePanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'climate-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const tabBar = document.createElement('div');
  tabBar.style.cssText = `
    display: flex; gap: 4px; padding: 8px 12px; flex-wrap: wrap;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(tabBar);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeTab: ClimateCategory = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderTabs(): void {
    tabBar.textContent = '';
    for (const tab of CATEGORY_TABS) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 11px; cursor: pointer; transition: all 150ms;
        background: ${activeTab === tab.value ? 'var(--accent)' : 'var(--bg-secondary)'};
        color: ${activeTab === tab.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeTab = tab.value;
        renderTabs();
        scheduleRender();
      };
      tabBar.appendChild(btn);
    }
  }

  function render(): void {
    const events = appStore.getState().events;
    const allClimate = events.filter(e => CLIMATE_SOURCES.has(e.source));

    let filtered = activeTab === 'all'
      ? allClimate
      : allClimate.filter(e => inferClimateCategory(e) === activeTab);

    // Sort by severity (critical first) then by recency
    filtered = filtered.slice().sort((a, b) => {
      const sevDiff = (SEVERITY_RANK[b.severity ?? 'info'] ?? 0) - (SEVERITY_RANK[a.severity ?? 'info'] ?? 0);
      if (sevDiff !== 0) return sevDiff;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

    contentArea.textContent = '';

    // Category count summary
    const summaryHeader = document.createElement('div');
    summaryHeader.style.cssText = `
      margin-bottom: 12px; padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const totalRow = document.createElement('div');
    totalRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${filtered.length > 0 ? 'var(--severity-medium)' : 'var(--text-muted)'};`;
    countNum.textContent = String(filtered.length);
    totalRow.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `environmental event${filtered.length !== 1 ? 's' : ''}`;
    totalRow.appendChild(countLabel);

    summaryHeader.appendChild(totalRow);

    // Breakdown badges
    if (activeTab === 'all' && allClimate.length > 0) {
      const breakdownRow = document.createElement('div');
      breakdownRow.style.cssText = 'display: flex; gap: 6px; flex-wrap: wrap;';

      const catCounts = new Map<ClimateCategory, number>();
      for (const ev of allClimate) {
        const cat = inferClimateCategory(ev);
        catCounts.set(cat, (catCounts.get(cat) ?? 0) + 1);
      }

      for (const tab of CATEGORY_TABS) {
        if (tab.value === 'all') continue;
        const count = catCounts.get(tab.value) ?? 0;
        if (count === 0) continue;

        const chip = document.createElement('span');
        chip.style.cssText = `
          font-size: 10px; padding: 2px 6px; border-radius: 3px;
          background: var(--bg-primary); color: var(--text-secondary);
        `;
        chip.textContent = `${CATEGORY_ICONS[tab.value] ?? ''} ${tab.label}: ${count}`;
        breakdownRow.appendChild(chip);
      }

      summaryHeader.appendChild(breakdownRow);
    }

    contentArea.appendChild(summaryHeader);

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No environmental events detected';
      contentArea.appendChild(msg);
      return;
    }

    for (const ev of filtered.slice(0, 80)) {
      const sev = ev.severity ?? 'info';
      const cat = inferClimateCategory(ev);
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary);
        border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        cursor: ${ev.location ? 'pointer' : 'default'};
      `;

      if (ev.location) {
        row.onclick = () => {
          const map = getMap();
          if (map && ev.location) {
            map.flyTo({
              center: [ev.location.lng, ev.location.lat],
              zoom: Math.max(map.getZoom(), 6),
              duration: 600,
            });
          }
        };
        row.title = 'Click to fly to location';
      }

      // Title row: icon + title + severity
      const titleRow = document.createElement('div');
      titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px;';

      const icon = document.createElement('span');
      icon.style.cssText = 'font-size: 12px; flex-shrink: 0;';
      icon.textContent = CATEGORY_ICONS[cat] ?? '';
      titleRow.appendChild(icon);

      const titleEl = document.createElement('span');
      titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
      titleEl.textContent = ev.title;
      titleRow.appendChild(titleEl);

      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px;
        text-transform: uppercase; flex-shrink: 0;
        background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'}22;
        color: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
      `;
      sevBadge.textContent = sev;
      titleRow.appendChild(sevBadge);

      row.appendChild(titleRow);

      // Meta row: location + timestamp
      const metaRow = document.createElement('div');
      metaRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 2px;';

      if (ev.location?.name || ev.location?.country) {
        const locEl = document.createElement('span');
        locEl.style.cssText = 'font-size: 10px; color: var(--text-secondary);';
        locEl.textContent = ev.location.name ?? ev.location.country ?? '';
        metaRow.appendChild(locEl);
      }

      const timeEl = document.createElement('span');
      timeEl.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-left: auto;';
      timeEl.textContent = relativeTime(ev.timestamp);
      metaRow.appendChild(timeEl);

      row.appendChild(metaRow);

      contentArea.appendChild(row);
    }
  }

  renderTabs();
  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
